import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconPencil, IconTrash } from '../primitives/Icons';

interface RowMenuProps {
  /** Viewport-space rect of the anchor button (from getBoundingClientRect). */
  anchorRect: { left: number; top: number; width: number; height: number };
  onRename: () => void;
  onDelete: () => void;
  onClose: () => void;
}

const POPOVER_WIDTH = 168;
const VIEWPORT_PADDING = 8;

/**
 * Desktop right-click / ⋯-button popover. Positioned with `position: fixed`
 * relative to the anchor's viewport rect. Closes on outside click or Escape.
 */
export function RowMenu({ anchorRect, onRename, onDelete, onClose }: RowMenuProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // mousedown so the popover closes before any underlying click fires.
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Anchor below the trigger and right-align with it; clamp into the viewport.
  const top = anchorRect.top + anchorRect.height + 4;
  const right = anchorRect.left + anchorRect.width;
  const left = Math.max(VIEWPORT_PADDING, Math.min(
    window.innerWidth - POPOVER_WIDTH - VIEWPORT_PADDING,
    right - POPOVER_WIDTH,
  ));

  return (
    <div
      ref={ref}
      className="row-popover"
      style={{ position: 'fixed', top, left }}
      role="menu"
    >
      <button
        type="button"
        className="row-popover-item"
        onClick={onRename}
        role="menuitem"
      >
        <IconPencil />
        <span>{t('rename')}</span>
      </button>
      <button
        type="button"
        className="row-popover-item row-popover-danger"
        onClick={onDelete}
        role="menuitem"
      >
        <IconTrash />
        <span>{t('deleteConversation')}</span>
      </button>
    </div>
  );
}
